import { BaseInputProps } from 'base-components/BaseInput/BaseInput';
import {
    dateFormat,
    dateFormat as globalDateFormat,
    datetimeFormat,
    timeFormat as globalTimeFormat,
} from 'utils/consts';
import { format, getMonth, getYear, isMatch } from 'date-fns';
import { range } from 'lodash';
import { Container } from 'models/common';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDOM from 'react-dom';
import { RefCallBack } from 'react-hook-form/dist/types';
import { HiPlus } from 'react-icons/hi';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { getTarget } from 'utils/DOMUtils';
import { toInt } from 'utils/number';
import './BaseDatePicker.scss';
import BaseDatePickerInput from './BaseDatePickerInput';


export type BaseDatePickerProps<T, AllowNullReturn extends boolean = false> = {
    id?: string;
    value?: Date | string | undefined | null;
    onChange?: (
        dateStr: AllowNullReturn extends true ? string | null : string,
        date: AllowNullReturn extends true ? Date | null : Date
    ) => void;
    timeInputLabel?: string;
    dateFormat?: string;
    displayDateFormat?: string;
    timeFormat?: string;
    disabled?: boolean;
    inputProps?: BaseInputProps<T>;
    container?: Container;
    timeOnly?: boolean;
    dateOnly?: boolean;
    invalid?: boolean;
    invalidMessage?: string;
    refCallback?: RefCallBack;
    allowClean?: AllowNullReturn;
    maxDate?: Date | null;
    minDate?: Date | null;
};

const getValue = (
    timeOnly?: boolean,
    dateOnly?: boolean,
    value?: Date | string | undefined | null
) => {
    if (value === null) {
        return value;
    }

    if (value instanceof Date) {
        return value;
    }

    const dateVal = new Date(
        timeOnly ? format(new Date(), globalDateFormat + "'T'") + value : value ?? ''
    );

    if (isNaN(dateVal.getTime())) {
        return dateVal;
    }

    return new Date(format(dateVal, dateOnly ? dateFormat : datetimeFormat));
};

const BaseDatePicker = <T, AllowNullReturn extends boolean = false>({
    id = '',
    value = null,
    onChange,
    timeInputLabel = '',
    displayDateFormat = 'MM/dd/yyyy HH:mm',
    timeFormat = 'H:m',
    dateFormat = datetimeFormat,
    disabled = false,
    inputProps,
    container,
    timeOnly,
    dateOnly,
    invalid,
    invalidMessage,
    refCallback,
    allowClean,
    ...props
}: BaseDatePickerProps<T, AllowNullReturn>) => {
    const [isFocus, setFocus] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>();
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);
    const years = range(1900, getYear(new Date()) + 100, 1);

    const months = useMemo(() => {
        return [...Array(12).keys()].map((x) =>
            new Intl.DateTimeFormat('ru', { month: 'long' }).format(new Date(2000, x, 1))
        );
    }, []);

    const handleCalendarClose = () => {
        setFocus(false);
    };

    const handleCalendarOpen = () => {
        setFocus(true);
    };

    const className = 'base-date-picker';

    const change = (date: Date | null, e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const inputValue = (e?.target as HTMLInputElement)?.value;
        if (onChange && (!inputValue || isMatch(inputValue, displayDateFormat))) {
            setCurrentValue(date);
            onChange(
                (date
                    ? format(
                          date,
                          timeOnly
                              ? globalTimeFormat + ':00'
                              : dateOnly
                                ? globalDateFormat
                                : dateFormat
                      )
                    : null) as any,
                (date ? new Date(format(date, dateFormat)) : null) as any
            );
        }
    };

    const popperContainer = useCallback(
        ({ children }: PropsWithChildren) => {
            const popper = <div className={className + '__popper-container'}>{children}</div>;

            if (container) {
                const target = getTarget(container);

                if (target) {
                    return ReactDOM.createPortal(popper, target);
                }
            }
            return popper;
        },
        [container]
    );

    const renderCustomHeader = useCallback(
        ({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
        }: ReactDatePickerCustomHeaderProps) => (
            <div className="header">
                <div>
                    <select
                        className="custom-scrollbar"
                        value={months[getMonth(date)]}
                        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                    >
                        {months.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <select
                        className="custom-scrollbar"
                        value={getYear(date)}
                        onChange={({ target: { value } }) => changeYear(toInt(value) ?? 0)}
                    >
                        {years.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                    >
                        <IoIosArrowBack />
                    </button>

                    <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                    >
                        <IoIosArrowForward />
                    </button>
                </div>
            </div>
        ),
        []
    );

    return (
        <div className={className + '__container'}>
            <DatePicker
                wrapperClassName="base-date-picker-wrapper"
                showTimeSelectOnly={timeOnly}
                id={id}
                selected={getValue(timeOnly, dateOnly, currentValue)}
                onChange={change}
                onCalendarClose={handleCalendarClose}
                onCalendarOpen={handleCalendarOpen}
                showTimeInput={!dateOnly}
                timeInputLabel={timeInputLabel}
                timeFormat={timeFormat}
                dateFormat={displayDateFormat}
                popperPlacement="bottom-start"
                fixedHeight
                showPopperArrow={false}
                popperContainer={popperContainer}
                customInput={
                    <BaseDatePickerInput
                        inputProps={inputProps}
                        disabled={disabled}
                        id={id}
                        invalid={invalid}
                        refCallback={refCallback}
                        inputRef={inputRef}
                        invalidMessage={invalidMessage}
                        isFocus={isFocus}
                    />
                }
                disabled={disabled || inputProps?.loading}
                popperClassName="base-date-picker__popper"
                renderCustomHeader={renderCustomHeader}
                {...props}
            />
            {allowClean && !disabled && (
                <HiPlus className={className + '__cross'} onClick={(e) => change(null)} />
            )}
        </div>
    );
};

export default BaseDatePicker;
